import React, {useCallback, useRef} from 'react';
import clsx from 'clsx';
import {type PopoverProps, popoverClasses} from '@mui/material';
import {type ReactAction as Action} from '@netx/core/bootstrap';
import {ActionMenuContainer} from '@netx/core-components/ActionMenu';
import {
	composeClasses,
	styled,
	WithClasses,
	WithMuiPropOverrides,
} from '@netx/core-components/Theme';
import {useWhenChangedEffect} from '../hooks';
import {
	contextMenuClasses,
	getContextMenuUtilityClass,
	ContextMenuClasses,
	CONTEXT_MENU_CLASS_NAME,
} from './contextMenuClasses';

type ContextMenuHeaderSeverity =
	| 'default'
	| 'primary'
	| 'secondary'
	| 'inherit'
	| 'error'
	| 'info'
	| 'success'
	| 'warning';

type ContextMenuOwnerState = WithClasses<ContextMenuClasses>;

const useUtilityClasses = (ownerState: ContextMenuOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		list: ['list'],
		wrapper: ['wrapper'],
	};
	return composeClasses(slots, getContextMenuUtilityClass, classes);
};

const ContextMenuRoot = styled(ActionMenuContainer, {
	name: CONTEXT_MENU_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	// [`& .${popoverClasses.paper}`]: {
	// 	marginRight: theme.spacing(2),
	// 	minHeight: 0,
	// },
}));

export interface ContextMenuOptions {
	actions: Array<Action | Action[]>;
	headerIcon?: any;
	left: number;
	top: number;
	headerSeverity?: ContextMenuHeaderSeverity;
	headerPrimary?: React.ReactElement | string;
	headerSecondary?: React.ReactElement | string;
}

export interface ContextMenuProps
	extends ContextMenuOptions,
		Partial<
			Omit<
				WithMuiPropOverrides<PopoverProps>,
				| 'classes'
				| 'className'
				| 'anchorReference'
				| 'anchorPosition'
				| 'onExited'
				| 'open'
				| 'role'
			>
		>,
		WithClasses<
			ContextMenuClasses,
			{
				onExited(action?: Action): void;
				open: boolean;
			}
		> {}

export const ContextMenuPrimitive = ({
	classes: _classes,
	className,
	actions,
	headerSeverity = 'default',
	headerIcon,
	headerPrimary,
	headerSecondary,
	left,
	open,
	top,
	onExited,
	onClose,
	...props
}: ContextMenuProps) => {
	const selectedAction = useRef<Action>();

	const ownerState: ContextMenuOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const handleAction = useCallback(
		(event: any, action: Action) => {
			selectedAction.current = action;
			onClose?.({}, 'escapeKeyDown');
		},
		[actions],
	);

	const handleClickAway = useCallback(() => onClose?.({}, 'backdropClick'), []);

	const handleExited = useCallback(() => {
		onExited(selectedAction.current);
		selectedAction.current = undefined;
	}, [actions, onExited]);

	useWhenChangedEffect(
		open,
		useCallback((isOpen) => {
			if (!isOpen) return;

			const listener = (e: MouseEvent) => {
				e.preventDefault();
				e.stopPropagation();
				selectedAction.current = undefined;
				onClose?.({}, 'backdropClick');
			};

			document.addEventListener('contextmenu', listener);
			return () => {
				document.removeEventListener('contextmenu', listener);
			};
		}, []),
		{
			executeAtMount: true,
		},
	);

	if (left == null || top == null) return null;

	return (
		<ContextMenuRoot
			{...props}
			open={open}
			slotProps={{
				mobileMenu: {
					ModalProps: {
						onTransitionExited: handleExited,
					},
				},
				popoverProps: {
					className: clsx(classes.root, className),
					role: undefined,
					anchorReference: 'anchorPosition',
					anchorPosition: {left, top},
					TransitionProps: {
						...props.TransitionProps,
						onExited: handleExited,
					},
				},
			}}
			actions={actions}
			onAction={handleAction}
			title={headerPrimary}
			severity={headerSeverity}
			secondaryTitle={headerSecondary}
			icon={headerIcon}
			onClose={handleClickAway}
		/>
	);
};

export const ContextMenu = ContextMenuPrimitive;

export default ContextMenu;
